import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { CatalogItem } from '../models/reel.model';
import { getReelBackgroundTheme, reelBackgroundGradient, ReelBackgroundTheme } from './reel-theme';

/**
 * The Feather Reel end card ("Before you go"). Ported from iOS `ReelEndCard.swift`.
 * Presentational: the overlay resolves localized text and passes it in. The radial background is
 * built from the measured card width (iOS radius = width × 1.2), recomputed on resize.
 */
@Component({
    selector: 'app-reel-end-card',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './reel-end-card.component.html',
    styleUrls: ['./reel-end-card.component.scss'],
    standalone: false
})
export class ReelEndCardComponent implements AfterViewInit {
  @Input() heroUrl: string | null = null;
  @Input() logoUrl: string | null = null;
  @Input() title = '';
  @Input() description = '';
  @Input() watchAgainLabel = '';
  @Input() closeLabel = '';
  @Input() catalog: CatalogItem[] = [];

  @Input() set backgroundType(value: string | null | undefined) {
    this.theme = getReelBackgroundTheme(value);
    this.updateBackground();
  }

  @Output() watchAgain = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  theme: ReelBackgroundTheme = getReelBackgroundTheme(null);
  background = reelBackgroundGradient(this.theme);
  scrimGradient = this.buildScrim(this.theme.scrim);

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.updateBackground();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateBackground();
  }

  get hasTitle(): boolean {
    return !!this.title && this.title.trim().length > 0;
  }

  get hasDescription(): boolean {
    return !!this.description && this.description.trim().length > 0;
  }

  private updateBackground(): void {
    const width = this.host?.nativeElement?.clientWidth ?? 0;
    this.background = reelBackgroundGradient(this.theme, width);
    this.scrimGradient = this.buildScrim(this.theme.scrim);
    this.cdr.markForCheck();
  }

  /** iOS bottom scrim: clear → scrim@0.42 → scrim → scrim, using 8-digit-hex alpha. */
  private buildScrim(scrim: string): string {
    return `linear-gradient(180deg, ${scrim}00 0%, ${scrim}6B 22%, ${scrim} 40%, ${scrim} 100%)`;
  }
}
